import { useId, useState } from 'react';
import { normalizeSearchText } from '../../utils/patientSearch';

type PatientOption = { id: string; displayName: string | null };

export default function FinancialSearchSelect({ options, value, disabled = false, onChange,
  label = 'Paciente', required = true, placeholder = 'Digite o nome do paciente…',
  emptyOption,
}: {
  options: PatientOption[];
  value: string;
  disabled?: boolean;
  onChange: (id: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  emptyOption?: { value: string; label: string };
}) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find(patient => patient.id === value);
  const terms = normalizeSearchText(query).split(' ').filter(Boolean);
  const matches = options.filter(patient => {
    const name = normalizeSearchText(patient.displayName ?? patient.id);
    return terms.every(term => name.includes(term));
  });
  const results = emptyOption
    ? [{ id: emptyOption.value, displayName: emptyOption.label }, ...matches]
    : matches;
  function select(patient: PatientOption) {
    onChange(patient.id);
    setQuery('');
    setOpen(false);
  }
  return <div className="relative text-sm font-medium">
    <label htmlFor={id}>{label}{required ? ' *' : ''}</label>
    <input
      id={id} type="text" role="combobox" autoComplete="off" required={required} disabled={disabled}
      aria-expanded={open} aria-controls={`${id}-results`} aria-autocomplete="list"
      aria-activedescendant={open && results[activeIndex] ? `${id}-option-${activeIndex}` : undefined}
      className="mt-2 w-full rounded-md border border-gray-300 p-3 disabled:cursor-not-allowed disabled:bg-gray-100"
      placeholder={placeholder}
      value={open ? query : selected?.displayName ?? selected?.id ?? (value === emptyOption?.value ? emptyOption.label : '')}
      onFocus={() => { setQuery(''); setActiveIndex(0); setOpen(true); }}
      onClick={() => setOpen(true)}
      onBlur={() => { setOpen(false); setQuery(''); }}
      onChange={event => { setQuery(event.target.value); setActiveIndex(emptyOption ? 1 : 0); setOpen(true); onChange(emptyOption?.value ?? ''); }}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          setOpen(true);
          setActiveIndex(index => Math.max(0, Math.min(results.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
        } else if (event.key === 'Enter' && open) {
          event.preventDefault();
          if (results[activeIndex]) select(results[activeIndex]);
        } else if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          setQuery('');
        }
      }}
    />
    {open && <ul id={`${id}-results`} role="listbox" aria-label={label} className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
      {matches.length === 0 && <li role="presentation" className="px-3 py-2 font-normal text-gray-500">Nenhum resultado encontrado.</li>}
      {results.map((patient, index) => <li
        id={`${id}-option-${index}`} key={patient.id} role="option" aria-selected={patient.id === value}
        ref={element => { if (open && index === activeIndex) element?.scrollIntoView({ block: 'nearest' }); }}
        className={`cursor-pointer px-3 py-2 font-normal hover:bg-blue-50 ${index === activeIndex ? 'bg-blue-50 text-blue-900' : ''}`}
        onMouseDown={event => event.preventDefault()} onClick={() => select(patient)}
      >{patient.displayName ?? patient.id}</li>)}
    </ul>}
  </div>;
}
